import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) console.warn("VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set; see .env.example");

/** Browser client. All reads/writes are constrained by RLS (director/admin roles). */
export const supabase = createClient(url ?? "http://127.0.0.1:54321", anonKey ?? "missing", {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

export type UserRole = "director" | "admin" | "controller";
export interface Profile {
  id: string; full_name: string; email: string | null; role: UserRole; organisation_id?: string;
}

export interface EstimateInput {
  labour?: { shiftType?: string; hours?: number; crewSize?: number; crews?: number };
  services?: { code: string; quantity?: number; hours?: number }[];
}
export interface Lead {
  id: string; created_at: string; status: string; name: string; company: string | null;
  email: string | null; phone: string | null; services: string[]; site_address: string | null;
  message?: string | null; preferred_date?: string | null;
  source: string; estimate_input?: EstimateInput | null; estimate_result: { total?: number } | null;
  /** Answers to a configurable website form beyond the columns above. */
  details?: { key: string; label: string; value: string }[] | null;
}
export interface Quote {
  id: string; quote_number: string; status: string; total: number; valid_until: string | null; created_at: string;
  pdf_path?: string | null; job_title?: string | null; client_name?: string | null;
}
export interface Booking {
  id: string; status: string; shift_type: string; starts_at: string; ends_at: string; crew_size: number; crews: number;
  company: string | null; contact_name: string | null; contact_phone: string | null; site_address: string | null;
  /** Embedded when the query selects `allocations(crew_member_id)`. */
  allocations?: { crew_member_id: string }[];
  /** Set when staff confirmed it without a signed agreement. */
  confirm_override_reason?: string | null;
  confirm_override_at?: string | null;
}
export interface CrewMember {
  id: string; full_name: string; phone: string | null; active: boolean;
  /** Ticket ids from the client layer's field.tickets that this person holds. */
  tickets_held: string[];
  driver_licence_class: string | null;
  /** First day of the month they last worked or trained under their tickets. */
  last_worked_month: string | null;
  email?: string | null; profile_id?: string | null; notes?: string | null;
  /** Partner (no subcontractor pay or insurance check), and the latest insurance certificate's expiry. */
  is_partner?: boolean; public_liability_expiry?: string | null;
}

/* ---------- Jobs ---------- */

export type JobStatus = "planned" | "active" | "on_hold" | "completed" | "cancelled";
export type DocumentKind = "tmp" | "tgs" | "permit" | "swms" | "other";

export interface Job {
  id: string; reference: string; title: string; status: JobStatus;
  site_address: string | null; client_name: string | null; site_contact_name: string | null; site_contact_phone: string | null;
  work_type: string | null; permit_reference: string | null; permit_conditions: string | null;
  crew_notes: string | null; internal_notes: string | null; created_at: string;
  /** The quote the client signs for this job (signed agreements). */
  quote_id?: string | null;
  /** Approved hours as written for crew. */
  approved_hours?: string | null;
  /** Structured permit hours: [{ days: [0-6], start: "HH:MM", end: "HH:MM" }]. */
  permit_hours?: PermitWindow[] | null;
  /** The client account (client onboarding). */
  customer_id?: string | null;
  /** The client's written confirmation of the job, recorded once. */
  client_confirmation_note?: string | null;
  /** Printed on the SWMS and shown in the job pack. */
  nearest_hospital?: string | null;
  /** Replaces the SWMS's default scope line when filled. */
  scope_of_work?: string | null;
  /** Where the client's SWMS signing link goes by default. */
  site_contact_email?: string | null;
  client_confirmation_at?: string | null;
  client_confirmation_file_name?: string | null;
}
export interface PermitWindow { days: number[]; start: string; end: string }
export type JobFields = Omit<Job, "id" | "reference" | "created_at">;

export interface JobAllocation { id: string; crew_member_id: string; role: string; response?: "accepted" | "declined" | null; decline_reason?: string | null }
export interface JobShift {
  id: string; status: string; shift_type: string; starts_at: string; ends_at: string; crew_size: number;
  confirm_override_reason?: string | null;
  complete_override_reason?: string | null;
  allocations: JobAllocation[];
}
export interface JobDocument {
  id: string; job_id: string; kind: DocumentKind; title: string; version_label: string | null; file_name: string;
  content_type: string; size_bytes: number; requires_ack: boolean; superseded_by: string | null; created_at: string;
  /** Set when made from a SWMS template; blank job fields it printed as "confirm at the toolbox talk". */
  template_key?: string | null; missing_fields?: string[];
  uploaded_by?: string | null; uploader?: { full_name: string | null } | null;
}
export interface DocumentAck {
  document_id: string; crew_member_id: string; kind: "read" | "swms_signon"; signed_name: string | null; acknowledged_at: string;
}
export interface JobSummary extends Job {
  bookings: { id: string; starts_at: string; ends_at: string; status: string }[];
  job_documents: { id: string; superseded_by: string | null }[];
}
export interface JobDetail extends Job {
  bookings: JobShift[];
  job_documents: JobDocument[];
}

/* ---------- Crew view (API /crew) ---------- */

export interface PackShift {
  id: string; jobId: string | null; status: string; shiftType: string; startsAt: string; endsAt: string; crewSize: number;
  siteAddress: string | null; company: string | null; contactName: string | null; contactPhone: string | null; notes: string | null;
}
export interface MyShift extends PackShift {
  job: { id: string; reference: string; title: string; siteAddress: string | null } | null;
  documentsToSign: number;
  leadingHand?: boolean;
  /** Checklists I fill in (as leading hand) and their status. */
  checklists?: Partial<Record<"start" | "end", ChecklistStatus>> | null;
  /** With crew messages: my reply to the shift (null until I reply). */
  reply?: { response: "accepted" | "declined" | null; respondedAt: string | null; declineReason: string | null } | null;
}
export type ChecklistStatus = "not_started" | "started" | "complete";
export interface PackDocument {
  id: string; kind: DocumentKind; title: string; versionLabel: string | null; fileName: string; contentType: string; sizeBytes: number;
  createdAt: string; ackKind: "read" | "swms_signon" | null;
  ack: { kind: "read" | "swms_signon"; signedName: string | null; acknowledgedAt: string } | null;
}
export interface JobPack {
  shift: PackShift;
  job: {
    id: string; reference: string; title: string; status: string; siteAddress: string | null; clientName: string | null;
    siteContactName: string | null; siteContactPhone: string | null; workType: string | null; permitReference: string | null;
    permitConditions: string | null; crewNotes: string | null; approvedHours?: string | null; permitHours?: PermitWindow[] | null;
    nearestHospital?: string | null;
  } | null;
  crew: { crewMemberId: string; name: string; phone: string | null; role: string; response?: "accepted" | "declined" | null; respondedAt?: string | null; declineReason?: string | null }[];
  /** Crew messages settings for the pack (null when off). */
  messages?: { lateMinutes: number[] } | null;
  /** Set-up photos are taken on this site. */
  setupPhotos?: boolean;
  documents: PackDocument[];
  statements: Record<"read" | "swms_signon", string>;
  viewer: { crewMemberId: string | null; canSign: boolean; isLeadingHand?: boolean };
  /** Field operations (null when the client has none). */
  field?: PackField | null;
}

/* ---------- Field operations ---------- */

export type ChecklistKind = "start" | "end";
export type ChecklistItemKind = "check" | "time" | "permitHours" | "signoff" | "finishTimes" | "notes" | "docket" | "photos";
export interface ChecklistAnswer {
  id: string; label: string; kind: ChecklistItemKind; required: boolean; help?: string; link?: { href: string; label?: string }; timeLabel?: string;
  answer: "yes" | "no" | null; note: string | null; time: string | null;
  signoff: { name: string; signature: string; hoursConfirmed: string } | null;
  finishTimes: { crewMemberId: string; name: string; finishedAt: string }[] | null;
  text: string | null;
  /** The docket item: the docket in force when the checklist was submitted. */
  docket?: { id: string; number: string; status: string } | null;
  /** The photos item: the least asked for, and how many the shift had at submission. */
  minPhotos?: number;
  photoCount?: number;
}
export interface ChecklistRecord {
  id: string; bookingId: string; kind: ChecklistKind; replaces: string | null; correctionReason: string | null;
  items: ChecklistAnswer[]; context: { permit?: { approvedHours: string | null; withinHours: boolean | null } };
  crewMemberId: string | null; profileId: string | null; startedAt: string; submittedAt: string | null; updatedAt: string;
}
export interface PackChecklist { status: ChecklistStatus; items: ChecklistAnswer[]; current: ChecklistRecord | null; history: ChecklistRecord[] }
export interface PackField {
  checklists: Partial<Record<ChecklistKind, PackChecklist>>;
  permit: { approvedHours: string | null; permitHours: PermitWindow[] | null; withinHours: boolean | null };
  canComplete: boolean;
  canReport: boolean;
  crew: { crewMemberId: string; name: string; role: string }[];
}
/** A checklist row as the office reads it (RLS). */
export interface ShiftChecklistRow {
  id: string; booking_id: string; kind: ChecklistKind; replaces: string | null; correction_reason: string | null;
  items: ChecklistAnswer[]; context: ChecklistRecord["context"]; crew_member_id: string | null; started_at: string; submitted_at: string | null; updated_at: string;
}
export type IncidentKind = "incident" | "near_miss";
export type Injury = "none" | "first_aid" | "medical_treatment" | "hospital";
export interface IncidentPrompt { id: string; label: string; when: "always" | "notifiable"; phone?: string; doneAt: string | null }
/** API shape (camelCase). */
export interface Incident {
  id: string; reference: string; kind: IncidentKind; bookingId: string | null; jobId: string | null; occurredAt: string;
  location: string | null; description: string; peopleInvolved: string | null; injury: Injury; immediateAction: string | null;
  category: string | null; categoryLabel: string | null; categoryOther: string | null; notifiable: boolean; prompts: IncidentPrompt[];
  reporterName: string; reportedAt: string; status: "open" | "closed"; closedAt: string | null; officeNotes: string | null;
  insurerNotifiedOn: string | null; officeAlertSentAt: string | null;
  reclassifiedFrom?: string | null; reclassifyReason?: string | null; insurerClockFrom?: string | null;
}
/** Database shape (RLS reads by the office). */
export interface IncidentRow {
  id: string; reference: string; kind: IncidentKind; booking_id: string | null; job_id: string | null; occurred_at: string;
  location: string | null; description: string; people_involved: string | null; injury: Injury; immediate_action: string | null;
  category: string | null; category_label: string | null; category_other: string | null; notifiable: boolean; prompts: IncidentPrompt[];
  reporter_name: string; reported_at: string; status: "open" | "closed"; closed_at: string | null; office_notes: string | null;
  insurer_notified_on: string | null; office_alert_sent_at: string | null;
  /** Set on an incident reclassified from a near miss: the near miss, the reason, and the insurer clock start. */
  reclassified_from?: string | null; reclassify_reason?: string | null; insurer_clock_from?: string | null;
  jobs?: { reference: string; title: string } | null;
  incident_photos?: { id: string; file_name: string; content_type: string; created_at: string }[];
}
export interface RateCardRow {
  crew_size: number; day_rate: number; night_rate: number; weekend_rate: number; public_holiday_rate: number; travel_allowance: number;
}
export interface ServicePrice {
  code: string; description: string; unit: "each" | "hour"; price: number; min_quantity: number | null; notes: string | null;
}
export interface RateCard {
  id: string; name: string; effective_from: string; effective_to: string | null; gst_rate: number; notes: string | null;
  rate_card_rows: RateCardRow[]; service_prices: ServicePrice[];
}

export type OutboxStatus = "pending" | "sent" | "failed" | "dead";
export interface OutboxItem {
  id: string; event_type: string; status: OutboxStatus; attempts: number; next_attempt_at: string; last_error: string | null; created_at: string;
}
export interface OutboxHealth {
  adapter: string; counts: Record<OutboxStatus, number>; oldestPendingAt: string | null; items: OutboxItem[];
}
